import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { searchData, PlanType, CheckInDateType } from './type'

type DateInDateModalProps = {
  plan: PlanType
  checkInDate: CheckInDateType
  handleCloseModal: () => void
}

export const DateInDateModal = (props: DateInDateModalProps) => {
  const { plan, checkInDate, handleCloseModal } = props
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const goToReservationPage = () => {
    if (capacityError) return
    setIsLoading(true)

    // URLパラメータから現在の検索データを取得
    const urlParams = new URLSearchParams(window.location.search)
    const birthdaysParam = urlParams.get('birthdays') || ''

    const params = {
      plan_id: String(plan.planId),
      number_of_adults: urlParams.get('number_of_adults') || String(searchData.number_of_adults),
      number_of_preschoolers_1: urlParams.get('number_of_preschoolers_1') || String(searchData.number_of_preschoolers_1 || 0),
      number_of_students_1: urlParams.get('number_of_students_1') || String(searchData.number_of_students_1 || 0),
      number_of_study_abroad: urlParams.get('number_of_study_abroad') || String(searchData.number_of_study_abroad || 0),
      birthdays: birthdaysParam,
      start_of_stay: checkInDate.date,
      end_of_stay: checkInDate.endDate,
    }

    const query = new URLSearchParams(params).toString()
    window.location.href = '/reservations/new?' + query
  }

  const number = (value) => (value ? Number(value) : 0)

  const childrenLength = () => {
    // URLパラメータから現在の検索データを取得
    const urlParams = new URLSearchParams(window.location.search)
    return (
      number(urlParams.get('number_of_study_abroad') || searchData.number_of_study_abroad) +
      number(urlParams.get('number_of_preschoolers_1') || searchData.number_of_preschoolers_1) +
      number(urlParams.get('number_of_students_1') || searchData.number_of_students_1)
    )
  }

  // URLパラメータから大人の人数を取得
  const getAdultCount = () => {
    const urlParams = new URLSearchParams(window.location.search)
    return number(urlParams.get('number_of_adults') || searchData.number_of_adults)
  }

  // [issue#2291] 宿泊施設の定員（大人上限 / こども上限 / 合計上限）を超えていないか。
  // 定員が未設定（0）の観点はチェックしない（Room#over_capacity? と同じ緩和）。
  // 宿なしプラン（room なし）は定員の概念が無いため対象外。
  const adults = getAdultCount()
  const children = childrenLength()
  const room = plan.room
  const capacityError =
    !!room &&
    ((room.adultMaxCount > 0 && adults > room.adultMaxCount) ||
      (room.childrenMaxCount > 0 && children > room.childrenMaxCount) ||
      (room.roomMaxCount > 0 && adults + children > room.roomMaxCount))

  // 「大人2名・こども2名・合計3名」のような定員の説明文
  const capacityText =
    room &&
    [
      room.adultMaxCount > 0 && t('reservation_confirm.modal_booking.capacity_over.adults', { count: room.adultMaxCount }),
      room.childrenMaxCount > 0 &&
        t('reservation_confirm.modal_booking.capacity_over.children', { count: room.childrenMaxCount }),
      room.roomMaxCount > 0 && t('reservation_confirm.modal_booking.capacity_over.total', { count: room.roomMaxCount }),
    ]
      .filter(Boolean)
      .join(t('reservation_confirm.modal_booking.capacity_over.separator'))

  return (
    <div className="modal-backdrop fixed top-0 left-0 right-0 z-50 w-full overflow-x-hidden overflow-y-auto max-h-full h-full justify-center items-center flex md:inset-0">
      <div
        className="flex flex-col items-start p-6 bg-white rounded-lg w-[350px] md:w-[580px]"
        style={{ animation: '0.4s ease-in-out 0s 1 normal forwards running slide-down', maxHeight: '80vh', overflowY: 'auto' }}
      >
        <div className="flex flex-col items-start gap-4 w-full">
          <div className="flex items-start gap-3 w-full">
            <div className="flex-1 font-bold font-size-16">
              {t('reservation_confirm.modal_booking.title.text_1')}
              <br />
              {t('reservation_confirm.modal_booking.title.text_2')}
            </div>
            <button
              className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white p-1.5"
              type="button"
              style={{ marginTop: '-15px', marginRight: '-15px' }}
              onClick={handleCloseModal}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                  clipRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  fillRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div className="flex flex-col items-start gap-3 w-full">
            <div className="flex items-center gap-5 w-full">
              <div className="flex flex-col items-start gap-1 flex-1">
                <div className="text-green font-size-12 font-bold">{t('plan_detail.navigation.nursery_info')}</div>
                <div className="font-bold font-size-14">{plan.mainNurseryName}</div>
              </div>
              <div className="h-20">
                <img
                  src={plan.mainNurseryFirstFeatureImageUrl}
                  alt={plan.mainNurseryName}
                  className="reservation-confirm-image"
                />
              </div>
            </div>
            {plan.subNurseryName && (
              <div className="flex items-center gap-5 w-full">
                <div className="flex flex-col items-start gap-1 flex-1">
                  <div className="text-green font-size-12 font-bold">{t('plan_detail.navigation.nursery_info')}</div>
                  <div className="font-bold font-size-14">{plan.subNurseryName}</div>
                </div>
                <div className="h-20">
                  <img
                    src={plan.subNurseryFirstFeatureImageUrl}
                    alt={plan.subNurseryName}
                    className="reservation-confirm-image"
                  />
                </div>
              </div>
            )}
            {/* [FB-76] 部屋なしプラン（宿泊なし / School Fee Only）は宿泊施設情報を表示しない */}
            {plan.room && (
              <>
                <hr className="w-full border-gray-200 my-4"></hr>
                <div className="flex items-center gap-5 w-full">
                  <div className="flex flex-col items-start gap-1 flex-1">
                    <div className="text-green font-size-12 font-bold">{t('plan_detail.navigation.accommodation')}</div>
                    <div className="font-bold font-size-14">{plan.room.name}</div>
                  </div>
                  <div className="h-20">
                    <img src={plan.room.photos[0]?.url} alt={plan.room.name} className="reservation-confirm-image" />
                  </div>
                </div>
              </>
            )}
            <hr className="w-full border-gray-200 my-4" />
            <div className="flex flex-col items-start gap-1 w-full">
              <div className="text-green font-size-12 font-bold">{t('reservation_confirm.modal_booking.booking_info')}</div>
              <div className="font-size-12 text-black-600">
                {t('reservation_confirm.modal_booking.period')}
                {checkInDate.date.split('-').slice(1).map(Number).join('/') /* "YYYY-MM-DD" → "M/D" */}〜
                {checkInDate.endDate.split('-').slice(1).map(Number).join('/')}
                <br />
                {t('reservation_input.modal.plan_detail.people')}
                {t('reservation_input.modal.plan_detail.adult', { count: getAdultCount() })}
                {t('reservation_input.modal.plan_detail.child', { count: childrenLength() })}
              </div>
            </div>
            <hr className="w-full border-gray-200 my-4"></hr>
            <div className="inline-flex items-center gap-5">
              <div className="flex flex-col items-start gap-1">
                <div className="text-green font-size-12 font-bold">{t('reservation_confirm.price.total')}</div>
                <div className="font-bold font-size-14">
                  {checkInDate.price.toLocaleString('ja-JP')}
                  {t('reservation_confirm.modal_booking.currency')}
                </div>
              </div>
            </div>
          </div>
        </div>
        {capacityError && (
          <div className="mt-4 w-full rounded-lg p-3 font-size-12" style={{ backgroundColor: '#FEF3F2', color: '#B42318' }}>
            {t('reservation_confirm.modal_booking.capacity_over.message', { capacity: capacityText })}
          </div>
        )}
        {isLoading ? (
          <button
            type="button"
            className="box-border w-full text-gray-900 focus:outline-hidden hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-bold rounded-lg text-sm h-11 px-3 py-2 mt-6 dark:bg-gray-800 dark:text-white dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800 request-processing"
            disabled
            aria-busy="true"
          >
            <svg
              className="inline w-4 h-4 mr-3 text-black animate-spin"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="#E5E7EB"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentColor"
              />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            className={`box-border h-11 px-3 py-2 w-full rounded-lg mt-6 text-white font-size-14 font-bold ${
              capacityError ? 'bg-gray-400 cursor-not-allowed' : 'bg-green'
            }`}
            onClick={goToReservationPage}
            disabled={capacityError}
          >
            {t('reservation_confirm.modal_booking.button')}
          </button>
        )}
      </div>
    </div>
  )
}
